import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { MapPin, Navigation, CalendarIcon, Car, ArrowRight } from 'lucide-react';
import { vehicleTypes } from '../data/mock';
import { format } from 'date-fns';

const BookingForm = () => {
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    pickup: '',
    destination: '',
    vehicleType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    }, 1000);
  };

  return (
    <section id="booking" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
            Quick & Easy Booking
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            Book Your Ride in Minutes
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Fill in your travel details and we'll help you find the perfect vehicle for your journey.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-50 to-amber-50/50 rounded-3xl p-6 md:p-10 shadow-xl shadow-slate-200/50 border border-slate-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pickup Location */}
                <div className="space-y-2">
                  <Label htmlFor="pickup" className="text-slate-700 font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-500" />
                    Pickup Location
                  </Label>
                  <Input
                    id="pickup"
                    placeholder="Enter pickup location"
                    value={formData.pickup}
                    onChange={(e) => setFormData({ ...formData, pickup: e.target.value })}
                    className="h-12 bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500/20 rounded-xl"
                  />
                </div>

                {/* Destination */}
                <div className="space-y-2">
                  <Label htmlFor="destination" className="text-slate-700 font-medium flex items-center gap-2">
                    <Navigation className="w-4 h-4 text-amber-500" />
                    Destination
                  </Label>
                  <Input
                    id="destination"
                    placeholder="Enter destination"
                    value={formData.destination}
                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                    className="h-12 bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500/20 rounded-xl"
                  />
                </div>

                {/* Travel Date */}
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-amber-500" />
                    Travel Date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal bg-white border-slate-200 hover:border-amber-500 rounded-xl"
                      >
                        {date ? format(date, 'PPP') : <span className="text-slate-400">Select date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Vehicle Type */}
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium flex items-center gap-2">
                    <Car className="w-4 h-4 text-amber-500" />
                    Vehicle Type
                  </Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, vehicleType: value })}>
                    <SelectTrigger className="h-12 bg-white border-slate-200 focus:border-amber-500 rounded-xl">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map((vehicle) => (
                        <SelectItem key={vehicle.value} value={vehicle.value}>
                          <span className="flex items-center justify-between w-full">
                            {vehicle.label}
                            <span className="text-amber-600 ml-2">{vehicle.price}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white text-lg font-semibold rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : submitted ? (
                  <span className="flex items-center gap-2">
                    Booking Request Sent!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Book Now
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </form>

            {/* Vehicle Type Tags */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex flex-wrap items-center justify-center gap-4">
                {vehicleTypes.map((vehicle) => (
                  <div key={vehicle.value} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200">
                    <Car className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600 text-sm">{vehicle.label}</span>
                    <span className="text-amber-600 font-semibold text-sm">{vehicle.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
